import i18nFactory from '@yandex-int/i18n';
import { cloneDeep, constant, range, times } from 'lodash';

import {
    DAYS_PER_WEEK,
    HOURS_PER_DAY,
    MINUTES_PER_HOUR
} from '../../constants';
import {
    TimeTargetingBoardItemRateCorrectionLevel,
    TimeTargetingDaysMenuItem,
    TimeTargetingEditorHolidaySettings,
    TimeTargetingEditorState,
    TimeTargetingHoursMenuItem,
    TimeTargetingHoursRange,
    TimeTargetingSelectedItem,
    TimeTargetingShowMenuItem,
    TimeTargetingTimeRange
} from '../../typings/timeTargetingEditor';
import {
    formatTimeNumberToString,
    formatTimeStringToNumber,
    getRoundedHoursByMinutes,
    getTimeStringWithFillingZeros
} from '../TimeHelper/TimeHelper';

import * as keyset from './TimeTargetingEditorHelper.i18n';
const i18n = i18nFactory(keyset);

export const NUMBER_LAST_WORKING_DAY = 5;
export const DEFAULT_RATE_CORRECTIONS_VALUE = 100;

export const listWeekDaysAbbreviation = [
    i18n('monday'),
    i18n('tuesday'),
    i18n('wednesday'),
    i18n('thursday'),
    i18n('friday'),
    i18n('saturday'),
    i18n('sunday')
];

export enum TimeTargetingDaysSelectType {
    DAILY = 'DAILY',
    WEEKDAYS = 'WEEKDAYS',
    WEEKENDS = 'WEEKENDS'
}

export const listTimeTargetingDaysMenuItems: TimeTargetingDaysMenuItem[] = [
    {
        id: TimeTargetingDaysSelectType.DAILY,
        text: i18n('daily'),
        listDays: range(DAYS_PER_WEEK)
    },
    {
        id: TimeTargetingDaysSelectType.WEEKDAYS,
        text: i18n('weekdays'),
        listDays: range(NUMBER_LAST_WORKING_DAY)
    },
    {
        id: TimeTargetingDaysSelectType.WEEKENDS,
        text: i18n('weekends'),
        listDays: range(NUMBER_LAST_WORKING_DAY, DAYS_PER_WEEK)
    }
];

export enum TimeTargetingHoursSelectType {
    HOUR24 = 'HOUR24',
    WORK_TIME = 'WORK_TIME',
    RANGE = 'RANGE',
    ARBITRARY = 'ARBITRARY'
}

export const listTimeTargetingHoursMenuItems: TimeTargetingHoursMenuItem[] = [
    {
        id: TimeTargetingHoursSelectType.HOUR24,
        text: i18n('hour24'),
        startHour: 0,
        endHour: 23
    },
    {
        id: TimeTargetingHoursSelectType.WORK_TIME,
        text: i18n('work-time'),
        startHour: 8,
        endHour: 19
    },
    {
        id: TimeTargetingHoursSelectType.RANGE,
        text: i18n('range-time'),
        startHour: 0,
        endHour: 23
    },
    {
        id: TimeTargetingHoursSelectType.ARBITRARY,
        text: i18n('arbitrary-time')
    }
];

export enum TimeTargetingShowSelectType {
    SHOW = 'SHOW',
    NOT_SHOW = 'NOT_SHOW'
}

export const listTimeTargetingShowMenuItems: TimeTargetingShowMenuItem[] = [
    {
        id: TimeTargetingShowSelectType.SHOW,
        text: i18n('show'),
        value: true
    },
    {
        id: TimeTargetingShowSelectType.NOT_SHOW,
        text: i18n('not-show'),
        value: false
    }
];

export const weekDaysList = times(DAYS_PER_WEEK, (value: number) => {
    return listWeekDaysAbbreviation[value];
});

/**
 * Метод возвращает уровень корректировки ставки по величине корректировки ставки
 * @param value {Number} величина корректировки ставки
 * @param enabledRateCorrectionMode {Boolean} включен ли режим корректировок ставок
 */
export const getTimeTargetingBoardItemRateCorrectionLevelByRateCorrectionPercent = (
    value: number,
    enabledRateCorrectionMode: boolean
): TimeTargetingBoardItemRateCorrectionLevel => {
    if (!enabledRateCorrectionMode && value !== 0) {
        return TimeTargetingBoardItemRateCorrectionLevel.two;
    }

    if (value === 0) {
        return TimeTargetingBoardItemRateCorrectionLevel.none;
    }

    if (value <= 50) {
        return TimeTargetingBoardItemRateCorrectionLevel.one;
    }

    if (value <= 100) {
        return TimeTargetingBoardItemRateCorrectionLevel.two;
    }

    if (value <= 150) {
        return TimeTargetingBoardItemRateCorrectionLevel.three;
    }

    return TimeTargetingBoardItemRateCorrectionLevel.four;
};

/**
 * Метолд проверяет находится ли элемент доски тайм таргетинга в заданной области
 * @param item {TimeTargetingSelectedItem} элемент доски тайм таргетинга
 * @param startPointArea {TimeTargetingSelectedItem} левая верхняя точка области
 * @param endPointArea {TimeTargetingSelectedItem} правая нижняя точка области
 */
export const isTimeTargetingBoardItemAtCurrentArea = (
    item: TimeTargetingSelectedItem,
    startPointArea: TimeTargetingSelectedItem,
    endPointArea: TimeTargetingSelectedItem
): boolean => {
    const { indexDay, indexHour } = item;
    const minX = Math.min(startPointArea.indexDay, endPointArea.indexDay);
    const maxX = Math.max(startPointArea.indexDay, endPointArea.indexDay);
    const minY = Math.min(startPointArea.indexHour, endPointArea.indexHour);
    const maxY = Math.max(startPointArea.indexHour, endPointArea.indexHour);

    return (
        indexDay >= minX &&
        indexDay <= maxX &&
        indexHour >= minY &&
        indexHour <= maxY
    );
};

/**
 * Метод подбирает корректный дневной диапазон для заданного списка дней
 * @param listIndexes {Number[]} список дней
 */
export const getSelectedDaysRangeForTimeTargeting = (
    listIndexes: number[]
): TimeTargetingDaysMenuItem | undefined => {
    let resultItem: TimeTargetingDaysMenuItem | undefined;

    for (let i = 0; i < listTimeTargetingDaysMenuItems.length; i++) {
        const { listDays } = listTimeTargetingDaysMenuItems[i];
        const isEqualLists =
            listIndexes.every(itemIndex => listDays.includes(itemIndex)) &&
            listIndexes.length === listDays.length;

        if (isEqualLists) {
            resultItem = listTimeTargetingDaysMenuItems[i];
            break;
        }
    }

    return resultItem;
};

/**
 * Метод получает заголовок дневного диапазона для заданного списка дней
 * @param listIndexes
 */
export const getTitleCurrentDaysRangeForTimeTargeting = (
    listIndexes: number[]
): string => {
    const foundedItem = getSelectedDaysRangeForTimeTargeting(listIndexes);
    let resultTitle = foundedItem ? foundedItem.text : undefined;

    if (resultTitle === undefined) {
        resultTitle = i18n('any-days-selected', {
            count: listIndexes.length
        });
    }

    return resultTitle;
};

/**
 * Метод отмечает область на доске тайм таргетинга с выбранным значением rateCorrectionValue
 * @param timeBoard {number[][]} доска тайм таргетинга
 * @param start {TimeTargetingSelectedItem} верхний левый элемент области
 * @param end {TimeTargetingSelectedItem} правый нижний элемент области
 * @param rateCorrectionValue {number} значение корректировки ставки
 */
export const selectAreaAtTimeTargetingBoard = (
    timeBoard: number[][],
    start: TimeTargetingSelectedItem,
    end: TimeTargetingSelectedItem,
    rateCorrectionValue: number
): number[][] => {
    const newTimeBoard = cloneDeep(timeBoard);

    for (let i = 0; i < timeBoard.length; i++) {
        newTimeBoard[i] = new Array(timeBoard[i].length);
        for (let j = 0; j < timeBoard[i].length; j++) {
            newTimeBoard[i][j] = timeBoard[i][j];

            if (
                isTimeTargetingBoardItemAtCurrentArea(
                    {
                        indexDay: i,
                        indexHour: j
                    },
                    start,
                    end
                )
            ) {
                newTimeBoard[i][j] = rateCorrectionValue;
            }
        }
    }

    return newTimeBoard;
};

/**
 * Метод проверяет есть ли в заданной области доски тайм таргетинга выбранные/не выбранные элементы
 * @param timeBoard {number[][]} доска тайм таргетинга
 * @param start {TimeTargetingSelectedItem} верхний левый элемент области
 * @param end {TimeTargetingSelectedItem} правый нижний элемент области
 * @param isSearchSelected {boolean} флаг характеризующий идет ли поиск по выбранным/не выбранным элементам
 */
export const hasNotSelectedItemsAtCurrentArea = (
    timeBoard: number[][],
    start: TimeTargetingSelectedItem,
    end: TimeTargetingSelectedItem,
    isSearchSelected: boolean
): boolean => {
    const minX = Math.min(start.indexDay, end.indexDay);
    const maxX = Math.max(start.indexDay, end.indexDay);
    const minY = Math.min(start.indexHour, end.indexHour);
    const maxY = Math.max(start.indexHour, end.indexHour);

    let result = false;

    for (let i = minX; i <= maxX; i++) {
        for (let j = minY; j <= maxY; j++) {
            if (
                (timeBoard[i][j] === 0 && !isSearchSelected) ||
                (timeBoard[i][j] !== 0 && isSearchSelected)
            ) {
                result = true;
            }
        }
    }

    return result;
};

/**
 * Метод проверяет есть ли в выбранной колонке доски тайм таргетинга выбранные/не выбранные элементы
 * @param timeBoard {number[][]} доска тайм таргетинга
 * @param indexColumn {number} индекс проверяемой колонки
 * @param isSearchSelected {boolean} флаг характеризующий идет ли поиск по выбранным/не выбранным элементам
 */
export const hasSpecificSelectedItemsTimeTargetingBoardByIndexColumn = (
    timeBoard: number[][],
    indexColumn: number,
    isSearchSelected: boolean
): boolean => {
    let result = false;

    for (let i = 0; i < timeBoard.length; i++) {
        if (
            (timeBoard[i][indexColumn] === 0 && !isSearchSelected) ||
            (timeBoard[i][indexColumn] !== 0 && isSearchSelected)
        ) {
            result = true;
        }
    }

    return result;
};

/**
 * Метод проверяет есть в выбранной строке доски тайм таргетинга выбранные/не выбранные элементы
 * @param timeBoard {number[][]} доска тайм таргетинга
 * @param indexRow {number} индекс проверяемой строки
 * @param isSearchSelected {boolean} флаг характеризующий идет ли поиск по выбранным/не выбранным элементам
 */
export const hasSpecificSelectedItemsTimeTargetingBoardByIndexRow = (
    timeBoard: number[][],
    indexRow: number,
    isSearchSelected: boolean
): boolean => {
    let result = false;

    for (let j = 0; j < timeBoard[0].length; j++) {
        if (
            (timeBoard[indexRow][j] === 0 && !isSearchSelected) ||
            (timeBoard[indexRow][j] !== 0 && isSearchSelected)
        ) {
            result = true;
        }
    }

    return result;
};

/**
 * Метод получает список выбранных строк (имеющих хотя бы 1 выбранный элемент) доски тайм таргетинга
 * @param timeBoard {number[][]} доска тайм таргетинга
 */
export const getListSelectedRowForTimeTargetingBoard = (
    timeBoard: number[][]
): number[] => {
    return timeBoard.reduce((acc: number[], row, index) => {
        if (
            hasSpecificSelectedItemsTimeTargetingBoardByIndexRow(
                timeBoard,
                index,
                true
            )
        ) {
            acc.push(index);
        }

        return acc;
    }, []);
};

/**
 * Метод получает список выбранных колонок (имеющих хотя бы 1 выбранный элемент) доски тайм таргетинга
 * @param timeBoard {number[][]} доска тайм таргетинга
 */
export const getListSelectedColumnForTimeTargetingBoard = (
    timeBoard: number[][]
): number[] => {
    const countColumns = timeBoard[0].length;
    const resultListIndexColumns: number[] = [];

    for (let i = 0; i < countColumns; i++) {
        if (
            hasSpecificSelectedItemsTimeTargetingBoardByIndexColumn(
                timeBoard,
                i,
                true
            )
        ) {
            resultListIndexColumns.push(i);
        }
    }

    return resultListIndexColumns;
};

/**
 * Метод начальной инициализации доски тайм таргетинга (полностью заполнена дефолтным значением)
 */
export const getInitialTimeBoard = (): number[][] => {
    const initialBoard = new Array(DAYS_PER_WEEK);

    for (let i = 0; i < initialBoard.length; i++) {
        initialBoard[i] = new Array(HOURS_PER_DAY);
        for (let j = 0; j < initialBoard[i].length; j++) {
            initialBoard[i][j] = DEFAULT_RATE_CORRECTIONS_VALUE;
        }
    }

    return initialBoard;
};

/**
 * Метод инициализации пустой доски тайм таргетинга (не выбран не один элемент)
 */
export const getEmptyTimeBoard = (): number[][] => {
    const timeBoard = new Array(DAYS_PER_WEEK);

    for (let i = 0; i < timeBoard.length; i++) {
        timeBoard[i] = times(HOURS_PER_DAY, constant(0));
    }

    return timeBoard;
};

/**
 * Метод возвращает дефолтное значение для настроек праздничных дней блока тайм таргетинга
 */
export function getDefaultTimeTargetingHolidaysSettings(): TimeTargetingEditorHolidaySettings {
    return {
        currentHoursRangeItem: listTimeTargetingHoursMenuItems[1],
        startTime: '00:00',
        endTime: '24:00',
        rateCorrections: DEFAULT_RATE_CORRECTIONS_VALUE,
        isShow: true
    };
}

/**
 * Метод возвращает дефолтное значение редактора тайм таргетинга
 */
export function getDefaultTimeTargetingEditorState(): TimeTargetingEditorState {
    return {
        timeBoard: getInitialTimeBoard(),
        useWorkingWeekends: true,
        isOpened: false,
        selectedDays: times(DAYS_PER_WEEK),
        currentDaysRangeItem: listTimeTargetingDaysMenuItems[0],
        currentHoursRangeItem: listTimeTargetingHoursMenuItems[0],
        rateCorrections: undefined,
        startTime: '00:00',
        endTime: '24:00',
        holidaysSettings: getDefaultTimeTargetingHolidaysSettings(),
        enabledHolidaysMode: false,
        idTimeZone: '',
        idTimeZoneGroup: ''
    };
}

/**
 * Метод вычисляет часовой интервал для заданной строки тайм таргетинга
 * (равен undefined, если интервал не сплошной, или не выбрано ни одного элемента)
 * @param timeRow {number[]} строка доски тайм таргетинга, характеризующая часовые интервалы
 */
export const getRangeByTimeRowTimeTargeting = (
    timeRow: number[]
): TimeTargetingHoursRange | undefined => {
    let firstNotEmptyIndex = -1;
    let lastNotEmptyIndex = -1;

    for (let i = 0; i < timeRow.length; i++) {
        if (timeRow[i] !== 0) {
            firstNotEmptyIndex = i;
            break;
        }
    }

    for (let j = timeRow.length - 1; j >= 0; j--) {
        if (timeRow[j] !== 0) {
            lastNotEmptyIndex = j;
            break;
        }
    }

    if (firstNotEmptyIndex === -1 || lastNotEmptyIndex === -1) {
        return undefined;
    }

    let hasEmptyItems = false;

    for (let i = firstNotEmptyIndex; i <= lastNotEmptyIndex; i++) {
        if (timeRow[i] === 0) {
            hasEmptyItems = true;
            break;
        }
    }

    if (hasEmptyItems) {
        return undefined;
    }

    return {
        startHour: firstNotEmptyIndex,
        endHour: lastNotEmptyIndex
    };
};

/**
 * Метод вычисляет общий временной интервал для доски тайм таргетинга
 * @param timeBoard {number[][]} доска тайм таргетинга
 */
export const getHoursRangeItemForTimeTargeting = (
    timeBoard: number[][]
): TimeTargetingHoursMenuItem => {
    const listSelectedRows = getListSelectedRowForTimeTargetingBoard(timeBoard);
    const listRangeHours = timeBoard.reduce(
        (acc: TimeTargetingHoursRange[], timeRow) => {
            const valueRange = getRangeByTimeRowTimeTargeting(timeRow);

            if (valueRange) {
                acc.push(valueRange);
            }

            return acc;
        },
        []
    );

    if (listSelectedRows.length !== listRangeHours.length) {
        return getHoursRangeItemByRange();
    }

    let isEqualAllRanges = true;

    for (let i = 1; i < listRangeHours.length; i++) {
        const currentItem = listRangeHours[i];
        const lastItem = listRangeHours[i - 1];

        if (
            currentItem.startHour !== lastItem.startHour ||
            currentItem.endHour !== lastItem.endHour
        ) {
            isEqualAllRanges = false;
            break;
        }
    }

    if (!isEqualAllRanges || listRangeHours.length === 0) {
        return {
            id: TimeTargetingHoursSelectType.ARBITRARY,
            text: i18n('arbitrary-time')
        };
    }

    const { startHour, endHour } = listRangeHours[0];

    return getHoursRangeItemByRange({
        startHour,
        endHour
    });
};

/**
 * Метод вычисляет временной интервал на основе часового интервала
 * @param hoursRange {TimeTargetingHoursRange} часовой интервал
 */
export const getHoursRangeItemByRange = (
    hoursRange?: TimeTargetingHoursRange
): TimeTargetingHoursMenuItem => {
    if (hoursRange !== undefined) {
        const { startHour, endHour } = hoursRange;
        for (let i = 0; i < listTimeTargetingHoursMenuItems.length; i++) {
            const menuItem = listTimeTargetingHoursMenuItems[i];
            switch (menuItem.id) {
                case TimeTargetingHoursSelectType.HOUR24:
                    if (
                        menuItem.startHour === startHour &&
                        menuItem.endHour === endHour
                    ) {
                        return menuItem;
                    }

                    break;

                case TimeTargetingHoursSelectType.WORK_TIME:
                    if (
                        menuItem.startHour === startHour &&
                        menuItem.endHour === endHour
                    ) {
                        return menuItem;
                    }

                    break;

                case TimeTargetingHoursSelectType.RANGE:
                    return {
                        ...menuItem,
                        startHour: startHour,
                        endHour: endHour
                    };

                case TimeTargetingHoursSelectType.ARBITRARY:
                    return menuItem;

                default:
                    break;
            }
        }
    }

    return {
        id: TimeTargetingHoursSelectType.ARBITRARY,
        text: i18n('arbitrary-time')
    };
};

/**
 * Метод подсчитывает количество рабочих дней для доски тайм таргетинга
 * @param timeBoard {number[][]} доска тайм таргетинга
 */
export const getCountWorkingDaysFotTimeTargeting = (
    timeBoard: number[][]
): number => {
    let countWorkingDays = 0;
    const lastWorkingDayIndex = Math.min(
        timeBoard.length,
        NUMBER_LAST_WORKING_DAY
    );

    for (let i = 0; i < lastWorkingDayIndex; i++) {
        for (let j = 0; j < timeBoard[i].length; j++) {
            if (timeBoard[i][j] !== 0) {
                countWorkingDays++;
            }
        }
    }

    return countWorkingDays;
};

/**
 * Метод вычисляет новое значение доски тайм таргетинга
 * @param timeBoard {number[][]} доска тайм таргетинга
 * @param selectedDays {number[]} список выбранных дней
 * @param hoursRangeItem {TimeTargetingHoursMenuItem} временной интервал
 * @param rateCorrectionsValue {number} текущая корректировка ставок
 */
export const getChangedTimeBoard = (
    timeBoard: number[][],
    selectedDays: number[],
    hoursRangeItem: TimeTargetingHoursMenuItem,
    rateCorrectionsValue: number
): number[][] => {
    let newTimeBoard = getEmptyTimeBoard();

    selectedDays.forEach(indexDay => {
        switch (hoursRangeItem.id) {
            case TimeTargetingHoursSelectType.HOUR24:
            case TimeTargetingHoursSelectType.WORK_TIME:
            case TimeTargetingHoursSelectType.RANGE:
                newTimeBoard = selectAreaAtTimeTargetingBoard(
                    newTimeBoard,
                    {
                        indexHour: hoursRangeItem.startHour,
                        indexDay: indexDay
                    },
                    {
                        indexHour: hoursRangeItem.endHour,
                        indexDay: indexDay
                    },
                    rateCorrectionsValue
                );

                break;

            case TimeTargetingHoursSelectType.ARBITRARY:
                newTimeBoard[indexDay] = [...timeBoard[indexDay]];

                break;

            default:
                break;
        }
    });

    return newTimeBoard;
};

/**
 * Метод преобразует целое число часов в формат '00:00'
 * @param hoursValue {number} число часов
 */
export const getTimeWithFillingZerosByHoursValue = (
    hoursValue: number
): string => {
    if (hoursValue < 10) {
        return getTimeStringWithFillingZeros('0' + hoursValue);
    }

    return getTimeStringWithFillingZeros(String(hoursValue));
};

/**
 * Метод получает временной интервал из элемента временного интервала
 * @param hoursRangeItem {TimeTargetingHoursMenuItem} временной интервал
 */
export const getTimeRangeByHoursRangeItem = (
    hoursRangeItem: TimeTargetingHoursMenuItem
): TimeTargetingTimeRange => {
    switch (hoursRangeItem.id) {
        case TimeTargetingHoursSelectType.HOUR24:
        case TimeTargetingHoursSelectType.WORK_TIME:
        case TimeTargetingHoursSelectType.RANGE:
            const { startHour, endHour } = hoursRangeItem;

            return {
                startTime: getTimeWithFillingZerosByHoursValue(startHour),
                endTime: getTimeWithFillingZerosByHoursValue(
                    getEndHourTimeFromHourIndexForTimeTargeting(endHour)
                )
            };

        case TimeTargetingHoursSelectType.ARBITRARY:
            return {
                startTime: getTimeWithFillingZerosByHoursValue(0),
                endTime: getTimeWithFillingZerosByHoursValue(HOURS_PER_DAY)
            };

        default:
            throw new Error(
                `Неподдерживаемый тип элемента быстрого выбора диапазон времени`
            );
    }
};

/**
 * Метод исправляет формат времени '12:35'
 * '12:35' -> '13:00'
 * undefined -> прошлое значение
 * '12' -> '12:00'
 * а также следит за тем, чтобы стартовое время не было больше конечного
 * @param startTime {string | undefined} стартовое время
 * @param endTime {string} конечное время
 * @param lastStartTime {string} прошлое значение стартового времени
 */
export const getCorrectStartTimeForTimeTargetingEditor = (
    startTime: string | undefined,
    endTime: string,
    lastStartTime: string
): string => {
    const newInputStartTime = getTimeStringWithFillingZeros(
        startTime || lastStartTime
    );
    const minutesForStartTime = formatTimeStringToNumber(newInputStartTime);
    const minutesForEndTime = formatTimeStringToNumber(endTime);

    if (minutesForStartTime === undefined || minutesForEndTime === undefined) {
        return '';
    }

    let hoursForStartTime = getRoundedHoursByMinutes(minutesForStartTime);
    const hoursForEndTime = getRoundedHoursByMinutes(minutesForEndTime);

    if (hoursForStartTime > hoursForEndTime) {
        hoursForStartTime = hoursForEndTime;
    }

    return formatTimeNumberToString(hoursForStartTime * MINUTES_PER_HOUR);
};

/**
 * Метод исправляет формат времени '12:35'
 * '12:35' -> '13:00'
 * undefined -> прошлое значение
 * '12' -> '12:00'
 * а также следит за тем, чтобы конечное время не было меньше начального
 * @param endTime {string | undefined} конечно время
 * @param startTime {string} начальное время
 * @param lastEndTime {string} прошлое значение конечного времени
 */
export const getCorrectEndTimeForTimeTargetingEditor = (
    endTime: string | undefined,
    startTime: string,
    lastEndTime: string
): string => {
    const newInputEndTime = getTimeStringWithFillingZeros(
        endTime || lastEndTime
    );
    const minutesForEndTime = formatTimeStringToNumber(newInputEndTime);
    const minutesForStartTime = formatTimeStringToNumber(startTime);

    if (minutesForStartTime === undefined || minutesForEndTime === undefined) {
        return '';
    }

    let hoursForEndTime = getRoundedHoursByMinutes(minutesForEndTime);
    const hoursForStartTime = getRoundedHoursByMinutes(minutesForStartTime);

    if (hoursForEndTime < hoursForStartTime) {
        hoursForEndTime = hoursForStartTime;
    }

    return formatTimeNumberToString(hoursForEndTime * MINUTES_PER_HOUR);
};

/**
 * Метод проверяет есть ли в таблице тайм таргетинга элементы отличные от 0 и дефолтного значения
 * @param timeBoard {number[][]} доска тайм таргетинга
 */
export const hasTimeTargetingBoardItemsWitModifiedRateCorrections = (
    timeBoard: number[][]
): boolean => {
    for (let i = 0; i < timeBoard.length; i++) {
        for (let j = 0; j < timeBoard[i].length; j++) {
            if (
                timeBoard[i][j] !== 0 &&
                timeBoard[i][j] !== DEFAULT_RATE_CORRECTIONS_VALUE
            ) {
                return true;
            }
        }
    }

    return false;
};

/**
 * Метод преобразования индекса времени окончания в число часов времени окончания
 * @param value
 */
export const getEndHourTimeFromHourIndexForTimeTargeting = (
    value: number
): number => {
    return value + 1;
};

/**
 * Метод преобразования числа часов времени окончания в индекс времени окончания
 * @param value
 */
export const getHourIndexFromEndHourTimeForTimeTargeting = (
    value: number
): number => {
    return Math.max(value - 1, 0);
};
